import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesAPI } from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

const COLORS = [
  { name: 'RED',    bg: '#FF6B6B', text: '#FF6B6B' },
  { name: 'BLUE',   bg: '#45AAF2', text: '#45AAF2' },
  { name: 'YELLOW', bg: '#FECA57', text: '#FECA57' },
  { name: 'GREEN',  bg: '#26de81', text: '#26de81' },
  { name: 'PURPLE', bg: '#A55EEA', text: '#A55EEA' },
  { name: 'ORANGE', bg: '#FF9F43', text: '#FF9F43' },
];

function makeRound() {
  const wordColor = COLORS[Math.floor(Math.random() * COLORS.length)];
  let inkColor = COLORS[Math.floor(Math.random() * COLORS.length)];
  const isMatch = Math.random() > 0.5;
  if (isMatch) inkColor = wordColor;
  return { wordColor, inkColor, correct: isMatch };
}

export default function ColorMatchScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();

  const [phase, setPhase] = useState('intro');
  const [round, setRound] = useState(makeRound());
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [timeLeft, setTimeLeft] = useState(3);
  const [total, setTotal] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(null);
  const timerRef = useRef(null);
  const MAX_ROUNDS = 10;

  const nextRound = useCallback(() => {
    setRound(makeRound());
    setTimeLeft(3);
  }, []);

  useEffect(() => {
    if (phase !== 'playing') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          const newLives = lives - 1;
          setLives(newLives);
          const newTotal = total + 1;
          setTotal(newTotal);
          if (newLives <= 0 || newTotal >= MAX_ROUNDS) {
            setPhase('over');
            saveResult(score);
          } else {
            setTimeout(nextRound, 400);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, round, lives, total, score, nextRound]);

  const handleAnswer = useCallback((userSaysMatch) => {
    if (phase !== 'playing') return;
    clearInterval(timerRef.current);
    const correct = userSaysMatch === round.correct;
    const newTotal = total + 1;
    setTotal(newTotal);
    if (correct) {
      setScore(s => s + (timeLeft * 10));
    } else {
      const newLives = lives - 1;
      setLives(newLives);
      if (newLives <= 0 || newTotal >= MAX_ROUNDS) {
        setPhase('over');
        saveResult(score + (correct ? timeLeft * 10 : 0));
        return;
      }
    }
    if (newTotal >= MAX_ROUNDS) {
      setPhase('over');
      saveResult(score + (correct ? timeLeft * 10 : 0));
    } else {
      setTimeout(nextRound, 300);
    }
  }, [phase, round, timeLeft, lives, total, score, nextRound]);

  const saveResult = async (finalScore) => {
    if (!profile) return;
    try {
      const res = await gamesAPI.saveResult({ profileId: profile.id, gameId: 'color-match', score: finalScore });
      setPointsEarned(res.pointsEarned);
      refreshProfile();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #FFEAA7, #FDCB6E)', paddingBottom: 20 }}>
        <button className="btn-back" onClick={() => navigate('/games')} style={{ background: 'rgba(255,255,255,0.4)', color: 'var(--dark)' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'var(--dark)', fontSize: '1.4rem' }}>🌈 Color Match</h1>
          {phase === 'playing' && (
            <div style={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)' }}>
              {'❤️'.repeat(lives)}{'🖤'.repeat(3 - lives)} · Score: {score}
            </div>
          )}
        </div>
      </div>

      <div className="screen-body" style={{ paddingTop: 30 }}>
        {phase === 'intro' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: 16 }}>🌈</div>
            <h2 style={{ fontFamily: 'var(--font-title)', marginBottom: 12 }}>Color Match</h2>
            <div className="card" style={{ background: '#FFFDE7', marginBottom: 24, textAlign: 'left' }}>
              <p style={{ lineHeight: 2, fontWeight: 600 }}>
                🎯 <strong>How to play:</strong><br/>
                Look at the <strong>color of the text</strong>.<br/>
                If the text color <strong>matches</strong> the word meaning → tap ✅<br/>
                If they are <strong>different</strong> → tap ❌<br/>
                Be fast! ⏱
              </p>
            </div>
            <div style={{ background: '#F0EBFF', borderRadius: 16, padding: 20, marginBottom: 24 }}>
              <p style={{ marginBottom: 8, fontWeight: 700 }}>Example:</p>
              <div style={{ fontSize: '2rem', color: '#FF6B6B', fontFamily: 'var(--font-title)', marginBottom: 4 }}>RED</div>
              <p style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>Text color = red ✅ (they match!)</p>
              <div style={{ fontSize: '2rem', color: '#45AAF2', fontFamily: 'var(--font-title)', marginTop: 12, marginBottom: 4 }}>GREEN</div>
              <p style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>Text color = blue ❌ (they don't match!)</p>
            </div>
            <button className="btn btn-primary btn-full btn-lg" onClick={() => setPhase('playing')}>
              Start Game! 🚀
            </button>
          </div>
        )}

        {phase === 'playing' && (
          <div style={{ textAlign: 'center' }}>
            <div className="progress-bar" style={{ marginBottom: 24, height: 16 }}>
              <div className="progress-bar-fill" style={{ width: `${(timeLeft / 3) * 100}%`, background: timeLeft <= 1 ? 'var(--red)' : 'linear-gradient(90deg, var(--purple), var(--pink))', transition: 'width 1s linear' }} />
            </div>
            <p style={{ color: 'var(--gray)', fontSize: '0.9rem', marginBottom: 8 }}>Does the text color match the word?</p>
            <div style={{ fontSize: '3.5rem', fontFamily: 'var(--font-title)', color: round.inkColor.text, marginBottom: 40, letterSpacing: 4, padding: '20px', background: 'var(--bg)', borderRadius: 20 }}>
              {round.wordColor.name}
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <button onClick={() => handleAnswer(true)} style={{ flex: 1, padding: '24px 16px', border: 'none', borderRadius: 20, background: '#26de81', color: 'white', fontFamily: 'var(--font-title)', fontSize: '1.5rem', cursor: 'pointer', boxShadow: '0 6px 0 #1FAF5F' }}>
                ✅ Yes!
              </button>
              <button onClick={() => handleAnswer(false)} style={{ flex: 1, padding: '24px 16px', border: 'none', borderRadius: 20, background: '#FF6B6B', color: 'white', fontFamily: 'var(--font-title)', fontSize: '1.5rem', cursor: 'pointer', boxShadow: '0 6px 0 #C0392B' }}>
                ❌ No!
              </button>
            </div>
            <div style={{ marginTop: 16, color: 'var(--gray)', fontSize: '0.85rem' }}>Question {total + 1}/{MAX_ROUNDS}</div>
          </div>
        )}

        {phase === 'over' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '5rem', marginBottom: 12 }}>{score >= 200 ? '🏆' : score >= 100 ? '🌟' : '💪'}</div>
            <h2 style={{ fontFamily: 'var(--font-title)', color: 'var(--purple)', fontSize: '1.8rem' }}>Game Over!</h2>
            <p style={{ color: 'var(--gray)', margin: '10px 0 20px' }}>Score: <strong>{score}</strong></p>
            {pointsEarned && (
              <div style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', borderRadius: 14, padding: 12, marginBottom: 20 }}>
                <strong>⭐ +{pointsEarned} points earned!</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setPhase('intro'); setScore(0); setLives(3); setTotal(0); setTimeLeft(3); setRound(makeRound()); setPointsEarned(null); }}>Play Again</button>
              <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => navigate('/games')}>Games</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
